import { useNavigate, useParams } from '@tanstack/react-router'
import { useAtom } from 'jotai'
import { useTranslation } from 'react-i18next'

import HeadsetMic from '@/components/assets/HeadsetMic'
import { Box } from '@/components/ui/box'
import { chatSettingsAtom, saveSettings } from '@/store/gigiChat/ChatAtom'
import { chatStatusAtom, getVisualAgentInfo, mapAgentColors } from '@/store/gigiChat/Visuals'
import { approvalsTotalAtom, compliancesTotalAtom } from '@/store/tasksApprovals'
import { IConversationSettings } from '@/types/gigiChat/ConversationSettings'
import { deepClone } from '@/utils'

import NavigationLink from '../link'

const Navigation = () => {
    const { t } = useTranslation()
    const navigate = useNavigate()
    const params = useParams({ from: '/chat/$chatId' })
    const chatIdParam: string = params.chatId
    const [tasksTotal] = useAtom(compliancesTotalAtom)
    const [approvalsTotal] = useAtom(approvalsTotalAtom)
    const [settings] = useAtom(chatSettingsAtom)
    const [chatStatus] = useAtom(chatStatusAtom)

    const updateSettings = () => {
        const newSettings = deepClone(settings) as IConversationSettings
        newSettings.processingStrategy = 'QA'

        saveSettings(newSettings)
    }

    const getChatIdsFromAsyncProcesses = () => {
        if (!chatStatus || !chatStatus.asyncProcesses || !chatStatus.asyncProcesses.length) return []

        const uniqueProcesses = chatStatus.asyncProcesses.filter(
            (process, index, self) => self.findIndex((p) => p.chatId === process.chatId) === index,
        )

        return uniqueProcesses.map((process) => {
            const status = process.data.status
            const agentInfo = getVisualAgentInfo(process?.data.settings.processingModeAgent?.name)
            const agentColor = mapAgentColors(agentInfo?.statusColors?.find((sc) => sc.status === status)?.color)

            return (
                <Box
                    key={process.chatId}
                    as="a"
                    onClick={() => (process.chatId === chatIdParam ? null : navigate({ to: `/chat/${process.chatId}` }))}
                    cursor={process.chatId === chatIdParam ? 'default' : 'pointer'}
                >
                    <HeadsetMic style={{ marginRight: '8px', display: 'inline-flex' }} color={agentColor} />
                </Box>
            )
        })
    }

    return (
        <Box mr="32px" className="gskNavigation" display={['none', 'none', 'none', 'block']}>
            <NavigationLink link="/" text={t('global.header.routes.main')} />
            <NavigationLink
                link="/chat"
                text={t('global.header.routes.chat')}
                onClick={() => {
                    updateSettings()
                }}
            />
            {getChatIdsFromAsyncProcesses()}
            <NavigationLink link="/translate" text={t('global.header.routes.translate')} />
            <NavigationLink link="/documents" text={t('global.header.routes.documents')} />
            <NavigationLink
                link="/tasks-approvals"
                text={t('global.header.routes.tasksApprovals')}
                totalNotifications={tasksTotal + approvalsTotal}
            />
        </Box>
    )
}

export default Navigation
